export * from '@sportworld/js-sdk/src/service/page'
import { api, soccerDataApi } from '@sportworld/js-sdk/src/https'
import {
  CreditsPurchaseType,
  CreditsPurchaseErrorType,
  PurchaseDataType,
} from '@sportworld/js-sdk/src/types/servicesTypes/walletTypes'
import {
  MatchCommentaryData,
  OptaMatchList,
  OptaTeamStandingsStage,
  RequestMatchesParams,
} from '@sportworld/js-sdk/src/types/soccerDataTypes'

import { commentaryTypes } from '@/constants/common'
import { LoginSessionType } from '@/constants/login'
import {
  CalendarType,
  ChannelType,
  ConfigType,
  EventType,
  OfferType,
  PromotionType,
  RegionIdType,
  RegionInfoType,
} from '@/types/servicesTypes/configTypes'
import {
  DeviceFlowSessionInfoType,
  DeviceFlowSessionStartType,
  PurchasePayloadType,
} from '@/types/servicesTypes/flowTypes'
import { UserResponseDataType } from '@/types/servicesTypes/identityTypes'
import {
  ChannelEventsBatch,
  ChannelEventsType,
  ChannelPlayback,
  ChannelsType,
  TilesType,
} from '@/types/servicesTypes/linesTypes'
import {
  BatchContextType,
  CataloguePagesType,
  GridsBatchDataType,
  LineContextType,
  LinesBatchDataType,
  PageType,
} from '@/types/servicesTypes/pageTypes'
import { LegalPagesType } from '@/types/servicesTypes/settingsTypes'
import { SportCategoryType } from '@/types/servicesTypes/sportCategoriesTypes'
import { CreditWalletType } from '@/types/servicesTypes/walletTypes'

const getSessionTypePath = (sessionType: LoginSessionType) => {
  return sessionType === LoginSessionType.REGISTER
    ? LoginSessionType.LOGIN
    : sessionType
}

export class ApiService {
  static async getBatchPages(pageIds: string[]): Promise<PageType[]> {
    const res = await api.post<PageType[]>(`/v3/pages/$batchGet`, pageIds)
    return res?.data
  }

  static async getHomePage(): Promise<PageType> {
    const res = await api.get<PageType>(`/v3/home/page`)
    return res?.data
  }

  static async getCalendarClassic(): Promise<CalendarType[]> {
    const res = await api.get<CalendarType[]>(`/v3/calendarClassic`)
    return res.data
  }

  static async getPageByTeamId(
    id: string,
    abortSignal?: AbortSignal,
  ): Promise<PageType> {
    const res = await api.get<PaginatedData<PageType>>(
      `/v3/pages?teamId=${id}`,
      {
        signal: abortSignal,
      },
    )
    return res?.data.items[0]
  }

  static async getSelectedPage(abortSignal?: AbortSignal): Promise<PageType> {
    const res = await api.get<PageType>(`/v3/selected/page`, {
      signal: abortSignal,
    })
    return res.data
  }

  static async getBatchGridData(
    body: BatchContextType,
    layout: string,
    abortSignal?: AbortSignal,
  ): Promise<GridsBatchDataType> {
    const { rowNumber, columnNumber, mobile } = {
      rowNumber: 2,
      columnNumber: 3,
      mobile: false,
    }
    const res = await api.post<GridsBatchDataType>(
      `/v3/gridContexts/$batchGetTiles?rowNumber=${rowNumber}&columnNumber=${columnNumber}&layout=${layout}&mobile=${mobile}`,
      body,
      { signal: abortSignal },
    )
    return res?.data
  }

  static async getCataloguePages(limit: number): Promise<CataloguePagesType> {
    const res = await api.get<CataloguePagesType>(
      `/v3/catalogue/pages?limit=${limit}`,
    )
    return res?.data
  }

  static async getBatchLinesData(
    body: BatchContextType,
    abortSignal?: AbortSignal,
  ): Promise<LinesBatchDataType> {
    const res = await api.post<LinesBatchDataType>(
      `/v3/lineContexts/$batchGetTiles`,
      body,
      { signal: abortSignal },
    )
    return res?.data
  }

  static async getLineDataById(
    body: LineContextType,
    abortSignal?: AbortSignal,
  ): Promise<TilesType> {
    const res = await api.get<TilesType>(
      `/v3/lineContexts/${body.lineContextId}/tiles?utcOffsetMins=${body.utcOffsetMins}&limit=${body.limitTilesPerLine}&next=${body.next}`,
      { signal: abortSignal },
    )
    return res.data
  }

  static async getAppConfig(regionId?: string): Promise<ConfigType> {
    const regionPath = regionId ? `regions/${regionId}/` : ''
    const url = `/v3/${regionPath}appconfig/pages`
    const res = await api.get<ConfigType>(url)
    return res.data
  }

  static async getRegionInfo(): Promise<RegionInfoType> {
    const res = await api.get<RegionInfoType>('/v3/my/regionInfo')
    return res.data
  }

  static async getRegionById(id: string): Promise<RegionIdType> {
    const res = await api.get<RegionIdType>(`/v3/regions/${id}`)
    return res.data
  }

  static async getSportCategoryById(
    categoryId: string,
    abortSignal?: AbortSignal,
  ): Promise<SportCategoryType> {
    const res = await api.get<SportCategoryType>(
      `/v3/categories/${categoryId}`,
      { signal: abortSignal },
    )
    return res?.data
  }

  static async getAllChannels(limit?: number): Promise<ChannelsType> {
    const res = await api.get<ChannelsType>(`/v3/channels?limit=${limit}`)
    return res.data
  }

  static async getChannelById(channelId: string): Promise<ChannelType> {
    const res = await api.get<ChannelType>(`/v3/channels/${channelId}`)
    return res.data
  }

  static async getBatchChannelEvents(
    channelIds: string[],
    limit = 1,
  ): Promise<ChannelEventsBatch> {
    const res = await api.post<ChannelEventsBatch>(
      '/v3/channels/$batchGetEvents',
      { channelIds, limit },
    )

    return res.data
  }

  static async getEventsByChannelId(
    id: string,
    limit = 1,
  ): Promise<ChannelEventsType> {
    const res = await api.get<ChannelEventsType>(
      `/v3/channels/${id}/events?limit=${limit}`,
    )
    return res.data
  }

  static async getEventById(
    id: string,
    abortSignal?: AbortSignal,
  ): Promise<EventType> {
    const res = await api.get<EventType>(`/v3/events/${id}`, {
      signal: abortSignal,
    })
    return res?.data
  }

  static async getChannelSources(channelId: string): Promise<ChannelPlayback> {
    const res = await api.get<ChannelPlayback>(
      `/v3/channels/${channelId}/playback`,
    )
    return res.data
  }

  static async getEventSources(eventId: string): Promise<ChannelPlayback> {
    const res = await api.get<ChannelPlayback>(`/v3/events/${eventId}/playback`)
    return res.data
  }

  static async getImprintData(regionId: string): Promise<LegalPagesType> {
    const url = regionId
      ? `/v3/regions/${regionId}/legal/imprint`
      : '/v3/legal/imprint'
    return api.get(url)
  }

  static async getPrivacyData(regionId: string): Promise<LegalPagesType> {
    const url = regionId
      ? `/v3/regions/${regionId}/legal/privacy`
      : '/v3/legal/privacy'
    return await api.get(url)
  }

  static async getTermsConditions(regionId: string): Promise<LegalPagesType> {
    const url = regionId
      ? `/v3/regions/${regionId}/legal/terms`
      : '/v3/legal/terms'
    return api.get(url)
  }

  static async startDeviceFlowSession(
    sessionType: LoginSessionType,
    payload?: PurchasePayloadType,
  ): Promise<DeviceFlowSessionStartType> {
    const path = getSessionTypePath(sessionType)
    const body = payload ? payload : {}
    return api
      .post<DeviceFlowSessionStartType>(`/${path}/deviceflowsessions`, body)
      .then(({ data }) => {
        if (data.loginSessionType) {
          data.loginSessionType = data.loginSessionType.toLowerCase()
        }

        data.sessionType = data.sessionType.toLowerCase()

        return data
      })
      .catch((data) => data.response.data)
  }

  static async deviceFlowSessionInfo(
    sessionType: LoginSessionType,
    sessionId: string,
    token: string,
  ) {
    const path = getSessionTypePath(sessionType)
    const { data } = await api.get<DeviceFlowSessionInfoType | undefined>(
      `/${path}/deviceflowsessions/${sessionId}/info`,
      {
        headers: {
          'b1-session-token': token,
        },
      },
    )

    return data
  }

  static async deviceFlowSessionLogin(
    sessionId: string,
    token: string,
  ): Promise<UserResponseDataType> {
    const res = await api.post<UserResponseDataType>(
      `/login/deviceflowsessions/${sessionId}/login`,
      {
        headers: {
          'b1-session-token': token,
        },
      },
    )

    return res.data
  }

  static async getOffers(accessRightUniqueIds: string[] = []) {
    const res = await api.get<{ items: OfferType[] }>('/v3/offers', {
      params: { accessRightUniqueIds },
    })
    return res.data.items
  }

  static async getTvModelsWithPromotions() {
    const res = await api.get('/v3/promotions/tvmodels')
    return res.data.items
  }

  static async getPromotionByModelCodeId(modelCodeId: string) {
    const res = await api.post(`/v3/promotions/${modelCodeId}`)
    return res?.data
  }

  static async getCreditWallets(
    abortSignal?: AbortSignal,
  ): Promise<CreditWalletType[]> {
    const res = await api.get('/v3/my/creditWallets', {
      signal: abortSignal,
    })
    return res?.data
  }
  static async getCreditWalletById(
    walletId: string,
  ): Promise<CreditWalletType> {
    const res = await api.get(`/v3/my/creditWallets/${walletId}`)
    return res?.data
  }

  static async purchaseWithCredits(
    body: PurchaseDataType,
  ): Promise<CreditsPurchaseType | CreditsPurchaseErrorType> {
    const res = await api.post(`/v3/checkout/purchaseWithCredits`, body, {
      validateStatus: () => true,
    })
    return res?.data
  }

  static async getPromotions(
    partnerIds: string[],
    abortSignal?: AbortSignal,
  ): Promise<PaginatedData<PromotionType>> {
    const res = await api.get('/v3/promotions', {
      params: { active: true, partners: partnerIds },
      signal: abortSignal,
    })
    return res?.data
  }

  static async getPromotionById(
    id: string,
    abortSignal?: AbortSignal,
  ): Promise<PromotionType> {
    const res = await api.get(`/v3/promotions/${id}`, {
      params: { active: true },
      signal: abortSignal,
    })
    return res?.data
  }

  static async getPartnerPromotions(
    partnerId: string,
    abortSignal?: AbortSignal,
  ): Promise<PaginatedData<PromotionType>> {
    const res = await api.get(`/v3/partners/${partnerId}/promotions`, {
      params: { active: true },
      signal: abortSignal,
    })
    return res?.data
  }

  static async getPartnerPromotionById(
    partnerId: string,
    promotionId: string,
    abortSignal?: AbortSignal,
  ): Promise<PromotionType> {
    const res = await api.get(
      `/v3/partners/${partnerId}/promotions/${promotionId}?active=true`,
      { signal: abortSignal },
    )
    return res?.data
  }
}

export class SoccerDataService {
  static async getMatches(
    {
      contestantId,
      tournamentCalendarId,
      matchTimeRangeStart,
      matchTimeRangeEnd,
      live = true,
    }: RequestMatchesParams,
    abortSignal?: AbortSignal,
  ): Promise<OptaMatchList> {
    const params: Record<string, any> = {
      matchTimeRangeStart,
      matchTimeRangeEnd,
      live,
    }

    if (contestantId) {
      params.contestantId = contestantId
    }
    if (tournamentCalendarId) {
      params.tournamentCalendarId = tournamentCalendarId
    }

    const res = await soccerDataApi.get('/perform/soccerdata/fixtures', {
      params,
      signal: abortSignal,
    })
    return res?.data.match
  }

  static async getStandings(
    calendarId: string,
    abortSignal?: AbortSignal,
  ): Promise<OptaTeamStandingsStage> {
    const res = await soccerDataApi.get(
      `/perform/soccerdata/tournamentCalendars/${calendarId}/standings`,
      { signal: abortSignal },
    )
    const { stage, tournamentCalendar } = res.data
    return {
      division: stage?.[0]?.division || [],
      tournamentCalendar,
    }
  }

  static async getCommentary(
    matchId: string,
    commentaryType?: commentaryTypes,
    abortSignal?: AbortSignal,
  ): Promise<MatchCommentaryData> {
    const params =
      commentaryType === commentaryTypes.MANUAL
        ? {
            type: commentaryType.toLowerCase(),
          }
        : undefined

    const res = await soccerDataApi.get(
      `/perform/soccerdata/fixtures/${matchId}/commentary`,
      { params, signal: abortSignal },
    )
    return res?.data
  }
}
